// Ten places to work. A theme is a backdrop scene plus the materials that sit
// on it: tile glass, ink, an accent and the chart colours. People can bend any
// of it in the studio, so these are starting points, not cages.

import type { CustomTheme, FontVoice, SceneSpec, ThemeId, ThemeSettings, TileStyle } from "../../shared/types";
import { BUILT_IN_THEMES } from "../../shared/palettes";
import { TEXT_COLORS, TEXT_COLOR_BASE } from "../../shared/textColors";
import { mix, plan } from "./compose";

export interface ThemeDef {
  /** a built-in place's id, or a made theme's */
  id: string;
  name: string;
  blurb: string;
  mode: "dark" | "light";
  pixel: boolean; // the scene is pixel art
  font: FontVoice;
  bg: string; // the colour under the scene, and the colour of the browser chrome
  tile: [number, number, number];
  tileAlpha: number;
  blur: number;
  border: string;
  fg: string;
  fg2: string;
  fg3: string;
  accent: string;
  accentFg: string;
  ok: string;
  warn: string;
  bad: string;
  charts: string[];
  swatch: [string, string, string]; // for the theme picker: sky, ground, accent
  dim?: number;
  radius?: number;
  /** a made theme's scene, drawn by the composer; built-in places have their own */
  scene?: SceneSpec | null;
  /** the theme it was made from, for made themes and palettes */
  custom?: CustomTheme | null;
}

/** A built-in place: a theme with its own hand-drawn scene. */
export interface PlaceDef extends ThemeDef {
  id: ThemeId;
}

export const THEMES: PlaceDef[] = [
  {
    id: "meadow",
    name: "Meadow",
    blurb: "A pixel meadow under a full moon.",
    mode: "dark",
    pixel: true,
    font: "clean",
    bg: "#0b1530",
    tile: [13, 21, 44],
    tileAlpha: 0.8,
    blur: 14,
    border: "rgba(150, 178, 255, 0.17)",
    fg: "#eaf0ff",
    fg2: "#a3b3dc",
    fg3: "#6d7ca8",
    accent: "#a5bdff",
    accentFg: "#0b1530",
    ok: "#7fdcae",
    warn: "#f6d585",
    bad: "#ff8f8f",
    charts: ["#a5bdff", "#f6d585", "#7fdcae", "#f39ac0", "#c5a8ff"],
    swatch: ["#1a2f6b", "#1d4a4f", "#a5bdff"],
  },
  {
    id: "studio",
    name: "Studio",
    blurb: "Quiet graphite. Nothing but your work.",
    mode: "dark",
    pixel: false,
    font: "clean",
    bg: "#0d0e11",
    tile: [22, 23, 28],
    tileAlpha: 0.92,
    blur: 0,
    border: "rgba(255, 255, 255, 0.075)",
    fg: "#ededf0",
    fg2: "#a2a4ae",
    fg3: "#6c6e79",
    accent: "#f1f1f3",
    accentFg: "#0d0e11",
    ok: "#8fd6a6",
    warn: "#e9c46a",
    bad: "#f08a84",
    charts: ["#f1f1f3", "#8fd6a6", "#9ab4ff", "#e9c46a", "#f08a84"],
    swatch: ["#16171c", "#0d0e11", "#f1f1f3"],
  },
  {
    id: "ember",
    name: "Ember",
    blurb: "Warm dark gold, like the last hour of a fire.",
    mode: "dark",
    pixel: false,
    font: "editorial",
    bg: "#0c0906",
    tile: [26, 19, 11],
    tileAlpha: 0.8,
    blur: 16,
    border: "rgba(246, 213, 133, 0.14)",
    fg: "#f6ebd4",
    fg2: "#c4ad86",
    fg3: "#8b7757",
    accent: "#f6d585",
    accentFg: "#1b140c",
    ok: "#b5d98a",
    warn: "#f6b26b",
    bad: "#f08a6e",
    charts: ["#f6d585", "#e08a5c", "#b5d98a", "#7fb0b8", "#d9a0c8"],
    swatch: ["#3a2810", "#0c0906", "#f6d585"],
  },
  {
    id: "paper",
    name: "Paper",
    blurb: "Warm paper and good ink, for daylight.",
    mode: "light",
    pixel: false,
    font: "editorial",
    bg: "#f3efe5",
    tile: [255, 254, 250],
    tileAlpha: 0.96,
    blur: 0,
    border: "rgba(54, 44, 22, 0.1)",
    fg: "#1f1c16",
    fg2: "#5f594c",
    fg3: "#958e7e",
    accent: "#d9502b",
    accentFg: "#ffffff",
    ok: "#2f8f5b",
    warn: "#c48a16",
    bad: "#cc3b2f",
    charts: ["#d9502b", "#2e5d9f", "#2f8f5b", "#c48a16", "#8a5bbd"],
    swatch: ["#f3efe5", "#e7e0d0", "#d9502b"],
  },
  {
    id: "aurora",
    name: "Aurora",
    blurb: "Northern lights over a still ridge.",
    mode: "dark",
    pixel: false,
    font: "clean",
    bg: "#04121a",
    tile: [7, 22, 28],
    tileAlpha: 0.78,
    blur: 16,
    border: "rgba(120, 255, 214, 0.14)",
    fg: "#e8fff8",
    fg2: "#94cabc",
    fg3: "#5d8f86",
    accent: "#72f0c6",
    accentFg: "#04121a",
    ok: "#72f0c6",
    warn: "#f2d27a",
    bad: "#ff8c9a",
    charts: ["#72f0c6", "#8ab8ff", "#c79bff", "#f2d27a", "#ff8c9a"],
    swatch: ["#0b3a3a", "#04121a", "#72f0c6"],
  },
  {
    id: "summit",
    name: "Summit",
    blurb: "Sunrise on the mountain, in pixels.",
    mode: "dark",
    pixel: true,
    font: "clean",
    bg: "#2b1636",
    tile: [34, 17, 38],
    tileAlpha: 0.8,
    blur: 14,
    border: "rgba(255, 176, 160, 0.2)",
    fg: "#fff1ec",
    fg2: "#e2b3b6",
    fg3: "#a7798a",
    accent: "#ffa585",
    accentFg: "#2b1636",
    ok: "#9be0b0",
    warn: "#ffd47a",
    bad: "#ff8a8a",
    charts: ["#ffa585", "#ffd47a", "#9be0b0", "#b7a3ff", "#ff8fb8"],
    swatch: ["#f6936f", "#3b2147", "#ffa585"],
  },
  {
    id: "tide",
    name: "Tide",
    blurb: "A pixel sea at dusk, the sun going down.",
    mode: "dark",
    pixel: true,
    font: "clean",
    bg: "#1b1740",
    tile: [22, 18, 50],
    tileAlpha: 0.78,
    blur: 14,
    border: "rgba(255, 196, 138, 0.18)",
    fg: "#fff3e6",
    fg2: "#cdb6cf",
    fg3: "#8f80a8",
    accent: "#ffc48a",
    accentFg: "#1b1740",
    ok: "#8fe3c4",
    warn: "#ffd98a",
    bad: "#ff8f9c",
    charts: ["#ffc48a", "#8fc7ff", "#8fe3c4", "#ff9ec4", "#c8a8ff"],
    swatch: ["#ef8f74", "#2a2a6a", "#ffc48a"],
  },
  {
    id: "daydream",
    name: "Daydream",
    blurb: "A pixel summer afternoon. Clouds, hills, no hurry.",
    mode: "light",
    pixel: true,
    font: "clean",
    bg: "#8cc8f5",
    tile: [255, 255, 255],
    tileAlpha: 0.86,
    blur: 12,
    border: "rgba(20, 50, 90, 0.1)",
    fg: "#14233a",
    fg2: "#4b5d78",
    fg3: "#8291a8",
    accent: "#2f72f0",
    accentFg: "#ffffff",
    ok: "#1f9d62",
    warn: "#c98a12",
    bad: "#d8453b",
    charts: ["#2f72f0", "#f08a2f", "#1f9d62", "#c04fd8", "#d8453b"],
    swatch: ["#8cc8f5", "#5fae5a", "#2f72f0"],
  },
  {
    id: "space",
    name: "Deep space",
    blurb: "Stars, a nebula and a quiet planet.",
    mode: "dark",
    pixel: false,
    font: "clean",
    bg: "#05050c",
    tile: [11, 11, 24],
    tileAlpha: 0.76,
    blur: 16,
    border: "rgba(175, 160, 255, 0.16)",
    fg: "#efebff",
    fg2: "#a7a1cc",
    fg3: "#6c6791",
    accent: "#b4a2ff",
    accentFg: "#0b0a1c",
    ok: "#86e3b9",
    warn: "#f2cf7c",
    bad: "#ff8aa0",
    charts: ["#b4a2ff", "#7fd3ff", "#86e3b9", "#f2cf7c", "#ff8aa0"],
    swatch: ["#2a1d5c", "#05050c", "#b4a2ff"],
  },
  {
    id: "synth",
    name: "Synth",
    blurb: "A neon sunset over an endless grid.",
    mode: "dark",
    pixel: false,
    font: "mono",
    bg: "#0c0418",
    tile: [17, 6, 32],
    tileAlpha: 0.8,
    blur: 12,
    border: "rgba(255, 92, 205, 0.24)",
    fg: "#ffeaff",
    fg2: "#c9a2d6",
    fg3: "#8a6a9e",
    accent: "#ff5ccd",
    accentFg: "#140520",
    ok: "#4de7ff",
    warn: "#ffd166",
    bad: "#ff6b8b",
    charts: ["#ff5ccd", "#4de7ff", "#ffd166", "#a78bfa", "#7dffb2"],
    swatch: ["#ff5ccd", "#2a0b4a", "#4de7ff"],
  },
];

export const THEME: Record<ThemeId, PlaceDef> = Object.fromEntries(THEMES.map((t) => [t.id, t])) as Record<ThemeId, PlaceDef>;

export const FONT_VOICES: { id: FontVoice; name: string; display: string; num: string; weight: number; tracking: string; sample: string }[] = [
  { id: "clean", name: "Clean", display: "'Geist Variable', 'Geist', system-ui, sans-serif", num: "'Geist Variable', 'Geist', system-ui, sans-serif", weight: 560, tracking: "-0.035em", sample: "Aa 8,420" },
  { id: "editorial", name: "Editorial", display: "'Instrument Serif', Georgia, serif", num: "'Instrument Serif', Georgia, serif", weight: 400, tracking: "-0.01em", sample: "Aa 8,420" },
  { id: "pixel", name: "Pixel", display: "'Pixelify Sans Variable', 'Pixelify Sans', monospace", num: "'Pixelify Sans Variable', 'Pixelify Sans', monospace", weight: 500, tracking: "0", sample: "Aa 8,420" },
  { id: "mono", name: "Mono", display: "'JetBrains Mono Variable', 'JetBrains Mono', monospace", num: "'JetBrains Mono Variable', 'JetBrains Mono', monospace", weight: 520, tracking: "-0.04em", sample: "Aa 8,420" },
];

export const ACCENTS = ["#a5bdff", "#72f0c6", "#f6d585", "#ffa585", "#ff5ccd", "#b4a2ff", "#7fd3ff", "#d9502b", "#2f72f0", "#f1f1f3"];

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

export function readableOn(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#000";
  // whichever of near-black and white reads better on it: a mid-tone takes black, where a fixed line gave white
  const l = luminance(rgb);
  return (l + 0.05) / (luminance([13, 14, 17]) + 0.05) >= 1.05 / (l + 0.05) ? "#0d0e11" : "#ffffff";
}

export function withAlpha(hex: string, a: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
}

// ---------------------------------------------------------------------------
// Made themes: the palettes, and every theme in this workspace. Compiled once
// per version into the same shape as a built-in place, with the contrast
// checked, so a theme from anywhere stays readable.

const MADE = new Map<string, CustomTheme>(BUILT_IN_THEMES.map((p) => [p.id, p]));
const COMPILED = new Map<string, ThemeDef>();
let madeVersion = 0;

/** The workspace's own themes. The palettes are always there. */
export function setMadeThemes(list: CustomTheme[]) {
  for (const k of [...MADE.keys()]) if (!k.startsWith("p:") && !k.startsWith("preview:")) MADE.delete(k);
  for (const t of list) MADE.set(t.id, t);
  madeVersion++;
}

/** A theme shown without being kept, such as one opened from a shared link. */
export function setPreviewTheme(t: CustomTheme | null) {
  for (const k of [...MADE.keys()]) if (k.startsWith("preview:")) MADE.delete(k);
  if (t) MADE.set(t.id, t);
  madeVersion++;
}

export function madeTheme(id: string | null | undefined): CustomTheme | null {
  return id ? (MADE.get(id) ?? null) : null;
}

function luminanceHex(hexc: string): number {
  const rgb = hexToRgb(hexc);
  return rgb ? luminance(rgb) : 0;
}

export function contrast(a: string, b: string): number {
  const la = luminanceHex(a);
  const lb = luminanceHex(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Moves a colour toward white (on dark) or black (on light) until it reads on the background. */
export function ensureContrast(fg: string, bg: string, min: number, dark: boolean): string {
  let c = fg;
  for (let i = 0; i < 24 && contrast(c, bg) < min; i++) c = mix(c, dark ? "#ffffff" : "#000000", 0.08);
  return c;
}

const SHADES = new Map<string, Record<string, string>>();

/** Each named text colour's shade on a theme's surface, readable on it. */
export function textShades(def: Pick<ThemeDef, "id" | "mode" | "fg" | "tile">): Record<string, string> {
  const key = `${def.id}:${def.mode}:${def.fg}:${def.tile.join(",")}`;
  const hit = SHADES.get(key);
  if (hit) return hit;
  const dark = def.mode !== "light";
  const surface = `#${def.tile.map((n) => Math.round(n).toString(16).padStart(2, "0")).join("")}`;
  const out: Record<string, string> = {};
  for (const name of TEXT_COLORS) out[name] = ensureContrast(mix(TEXT_COLOR_BASE[dark ? "dark" : "light"][name], def.fg, 0.08), surface, 4.5, dark);
  SHADES.set(key, out);
  return out;
}

/**
 * Colors the user chose for agents (Features 6, shared/authors.ts AgentColor), as the named ones are made: a touch of
 * the theme's ink, then moved until it reads on the theme's surface. Each as the variable the named ones use.
 */
export function customShades(def: Pick<ThemeDef, "mode" | "fg" | "tile">, colors: string[]): Record<string, string> {
  const dark = def.mode !== "light";
  const surface = `#${def.tile.map((n) => Math.round(n).toString(16).padStart(2, "0")).join("")}`;
  const out: Record<string, string> = {};
  for (const c of colors) if (/^x-[0-9a-f]{6}$/.test(c)) out[`--tc-${c}`] = ensureContrast(mix(`#${c.slice(2)}`, def.fg, 0.08), surface, 4.5, dark);
  return out;
}

export function compileTheme(t: CustomTheme): ThemeDef {
  const key = `${t.id}:${t.updatedAt}`;
  const hit = COMPILED.get(key);
  if (hit) return hit;
  const dark = t.mode !== "light";
  const c = t.colors;
  const glass = Math.max(0, Math.min(1, t.glass ?? 0.3));
  const tileAlpha = Math.round((1 - glass * 0.55) * 100) / 100;
  const p = plan(t.scene, { dark });
  const photo = t.backgrounds?.[0];
  // what text actually sits on: the tile, with a little of the scene (or the picture) through it
  const behind = photo ? (photo.tone ?? c.bg) : (p.sky[Math.floor(p.sky.length / 2)] ?? c.bg);
  const surface = mix(c.tile, behind, (1 - tileAlpha) * 0.7);
  const ink = ensureContrast(c.ink, surface, 7, dark);
  const ink2 = ensureContrast(c.ink2 ?? mix(ink, c.tile, 0.3), surface, 4.5, dark);
  const ink3 = ensureContrast(c.ink3 ?? mix(ink, c.tile, 0.5), surface, 3, dark);
  const accent = ensureContrast(c.accent, surface, 3, dark);
  const ok = ensureContrast(c.ok ?? (dark ? "#7fdcae" : "#2f8f5b"), surface, 3, dark);
  const warn = ensureContrast(c.warn ?? (dark ? "#f6d585" : "#b07a10"), surface, 3, dark);
  const bad = ensureContrast(c.bad ?? (dark ? "#ff8f8f" : "#cc3b2f"), surface, 3, dark);
  const fill = dark ? ["#f6d585", "#7fdcae", "#f39ac0", "#9ab4ff", "#c5a8ff"] : ["#c48a16", "#2f8f5b", "#c04fd8", "#2e5d9f", "#d8453b"];
  const charts = [accent, ...(c.charts ?? []).slice(1), ...fill].slice(0, 5).map((x, i) => (i === 0 ? x : ensureContrast(x, surface, 2.2, dark)));
  const tile = hexToRgb(c.tile) ?? [20, 20, 30];
  const def: ThemeDef = {
    id: t.id,
    name: t.name,
    blurb: t.blurb,
    mode: dark ? "dark" : "light",
    pixel: !photo && p.style === "pixel",
    font: t.font,
    bg: c.bg,
    tile,
    tileAlpha,
    blur: t.blur ?? (glass > 0.12 ? 14 : 0),
    border: c.border ? withAlpha(c.border, 0.45) : withAlpha(mix(accent, ink, 0.5), dark ? 0.16 : 0.14),
    fg: ink,
    fg2: ink2,
    fg3: ink3,
    accent,
    accentFg: readableOn(accent),
    ok,
    warn,
    bad,
    charts,
    swatch: [p.sky[1] ?? p.sky[0], p.layers[0]?.color ?? c.bg, accent],
    radius: t.radius,
    scene: t.scene,
    custom: t,
  };
  COMPILED.set(key, def);
  return def;
}

/** Any theme by id: a built-in place, a palette, or one this workspace made. */
export function themeDef(id: string | null | undefined): ThemeDef | null {
  if (!id) return null;
  if (Object.hasOwn(THEME, id)) return THEME[id as ThemeId];
  const made = MADE.get(id);
  return made ? compileTheme(made) : null;
}

/**
 * Whether a board shows one of its theme's photographs or paintings, rather
 * than a drawn scene or its own color or image: words written on the board
 * then keep a soft shade around them. The same for its owner and a visitor.
 */
export function showsPhoto(def: ThemeDef, backdrop: ThemeSettings["backdrop"]): boolean {
  return !!def.custom?.backgrounds?.length && (backdrop?.kind ?? "scene") === "scene";
}

/** A board's look with another theme tried on: a place by id, or a made theme as it was made. */
export function lookOf(base: ThemeSettings, id: string): ThemeSettings {
  if (Object.hasOwn(THEME, id)) return { ...base, id: id as ThemeId, custom: null, accent: null, backdrop: null, font: null, opacity: null, blur: null };
  return { ...base, custom: id, accent: null, backdrop: null, font: null, opacity: null, blur: null, radius: null };
}

export interface ResolvedTheme {
  def: ThemeDef;
  vars: Record<string, string>;
  tile: TileStyle;
  motion: boolean;
  grid: boolean;
  dim: number;
}

export function resolveTheme(s: ThemeSettings | null | undefined, opts: { reducedMotion?: boolean } = {}): ResolvedTheme {
  const made = s?.custom ? MADE.get(s.custom) : undefined;
  const def = (made ? compileTheme(made) : undefined) ?? (s?.id && Object.hasOwn(THEME, s.id) ? THEME[s.id] : undefined) ?? THEME.meadow;
  const accent = s?.accent && hexToRgb(s.accent) ? s.accent : def.accent;
  // what sits on the accent (Add's plus, a primary button's words) reads on it: a theme's own pair that doesn't
  // (a picture theme's soft accent with a soft partner) gives way to black or white, whichever reads (David, 16 Oct)
  const own = s?.accent && hexToRgb(s.accent) ? readableOn(s.accent) : def.accentFg;
  const accentFg = !hexToRgb(own) || !hexToRgb(accent) || contrast(own, accent) >= 4.5 ? own : readableOn(accent);
  // a hovered accent button moves away from its words, never toward them: lighter under dark words, darker under light
  const accentHover = hexToRgb(accentFg) && luminanceHex(accentFg) > 0.5 ? `color-mix(in srgb, ${accent}, black 14%)` : `color-mix(in srgb, ${accent}, white 14%)`;
  const tile: TileStyle = s?.tile ?? "glass";
  const alpha = s?.opacity ?? (tile === "solid" ? 0.97 : tile === "outline" ? 0.18 : def.tileAlpha);
  const blur = s?.blur ?? (tile === "solid" ? 0 : def.blur);
  const radius = s?.radius ?? def.radius ?? 14;
  const voice = FONT_VOICES.find((f) => f.id === (s?.font ?? def.font)) ?? FONT_VOICES[0];
  const light = def.mode === "light";
  // a typed board's surfaces: solid unless the user lets the theme through, and then blurred harder, with a
  // shade under the words, so they read on any picture
  const surface = typeof s?.surface === "number" && s.surface >= 0.4 && s.surface < 0.94 ? s.surface : 0.94;
  const through = surface < 0.9;
  const vars: Record<string, string> = {
    "--bg": def.bg,
    "--tile-rgb": def.tile.join(" "),
    "--tile-alpha": String(alpha),
    "--tile-blur": `${blur}px`,
    "--surface-alpha": String(surface),
    "--surface-blur": `${through ? Math.max(blur, 22) : blur}px`,
    "--surface-shade": through ? (light ? "0 1px 1px rgb(255 255 255 / 0.55), 0 0 10px rgb(255 255 255 / 0.35)" : "0 1px 2px rgb(0 0 0 / 0.45), 0 0 10px rgb(0 0 0 / 0.3)") : "none",
    "--tile-border": tile === "outline" ? withAlpha(def.fg, light ? 0.22 : 0.26) : def.border,
    "--radius": `${radius}px`,
    "--radius-sm": `${Math.max(4, Math.round(radius * 0.6))}px`,
    "--fg": def.fg,
    "--fg-2": def.fg2,
    "--fg-3": def.fg3,
    "--fg-4": withAlpha(def.fg, light ? 0.3 : 0.28),
    "--accent": accent,
    "--accent-fg": accentFg,
    "--accent-hover": accentHover,
    "--accent-soft": withAlpha(accent, light ? 0.12 : 0.16),
    "--accent-line": withAlpha(accent, 0.5),
    "--ok": def.ok,
    "--warn": def.warn,
    "--bad": def.bad,
    "--ok-soft": withAlpha(def.ok, 0.14),
    "--warn-soft": withAlpha(def.warn, 0.16),
    "--bad-soft": withAlpha(def.bad, 0.14),
    "--hover": light ? "rgba(20, 20, 30, 0.05)" : "rgba(255, 255, 255, 0.06)",
    "--press": light ? "rgba(20, 20, 30, 0.09)" : "rgba(255, 255, 255, 0.1)",
    "--line": light ? "rgba(20, 20, 30, 0.08)" : "rgba(255, 255, 255, 0.07)",
    "--line-2": light ? "rgba(20, 20, 30, 0.14)" : "rgba(255, 255, 255, 0.12)",
    "--field": light ? "rgba(20, 20, 30, 0.045)" : "rgba(255, 255, 255, 0.05)",
    "--shadow": light ? "0 1px 2px rgba(40, 30, 10, 0.06), 0 12px 32px -14px rgba(40, 30, 10, 0.22)" : "0 1px 0 rgba(255, 255, 255, 0.035) inset, 0 18px 40px -18px rgba(0, 0, 0, 0.65)",
    "--chrome-rgb": def.tile.join(" "),
    "--chrome-alpha": String(light ? 0.9 : Math.max(0.82, def.tileAlpha + 0.08)),
    "--chrome-border": light ? "rgba(20, 30, 50, 0.1)" : def.border,
    "--font-display": voice.display,
    "--font-num": voice.num,
    "--num-weight": String(voice.weight),
    // small numbers (lists, legends, tables) stay sturdy where the display face is a thin serif
    "--font-num-sm": voice.id === "editorial" ? FONT_VOICES[0].num : voice.num,
    "--num-sm-weight": String(voice.id === "editorial" ? 520 : voice.weight),
    "--num-tracking": voice.tracking,
    "--grid-dot": light ? "rgba(30, 30, 40, 0.13)" : "rgba(255, 255, 255, 0.07)",
    "--scroll-thumb": withAlpha(def.fg, light ? 0.2 : 0.16),
    "--scroll-thumb-hover": withAlpha(def.fg, light ? 0.34 : 0.3),
  };
  def.charts.forEach((c, i) => (vars[`--chart-${i + 1}`] = i === 0 ? accent : c));
  // the named text colours (Features 1, 20 Oct): each a touch of the theme's own ink, then moved until it reads on
  // the theme's surface (4.5 to 1, as body text must), so red is a readable red on every theme
  for (const [name, hex] of Object.entries(textShades(def))) vars[`--tc-${name}`] = hex;
  return {
    def,
    vars,
    tile,
    motion: (s?.motion ?? true) && !opts.reducedMotion,
    grid: s?.grid ?? (def.id === "studio" || def.id === "paper"),
    dim: s?.dim ?? def.dim ?? 0,
  };
}
